import { useMemo } from "react";
import { apiFetch } from "../../../../toolympus/api/core";
import { today } from "../../../../toolympus/api/datetimeUtil";
import { useItemActionWithConfirmation } from "../../../../toolympus/api/useAction";
import { useEditItem2 } from "../../../../toolympus/api/useNewItem";
import { useBrowserStoredValue, wrapBooleanFilter } from "../../../../toolympus/hooks/useBrowserStoredValue";
import { useLoadedList } from "../../../../toolympus/hooks/useLoadedList";
import { useUser } from "../../../../toolympus/userContext/UserContext";
import { useOrgStructure } from "../../Structure";
import { useManageOrgStructure } from "../../Structure/data/useManageOrgStructure";
import { Employee } from "../../Structure/types";
import { AbsenceEvent, AbsenceKindNote, AbsenceStats } from "./types";
import { useAbsenceEventBuckets } from "./useAbsenceEventBuckets";
import { useCalendarControl } from "./useCalendarControl";
import { useDepartmentStaffingWarnings } from "./useStaffingWarnings";
import { useAbsenceExtraQuotas } from "./useAbsenceExtraQuotas";

export const isAdmin = (u: any | undefined) => u?.roles?.includes("admin");

const AbsenceEventsApiPath = "/api/org/absence/event";

export const EmptyEvents: AbsenceEvent[] = [];

export const useAbsenceCalendar = () => {
  const calendarControl = useCalendarControl();
  const orgStructure = useOrgStructure();

  const orgStructureManagement = useManageOrgStructure(() => orgStructure.forceReload());

  const includeCancelled = useBrowserStoredValue("false", undefined, "__iclrcp_absence_include_cancelled");
  const onlyMyDepartmentFilter = useBrowserStoredValue("false", undefined, "__iclrcp_absence_only_mydept");

  const { user } = useUser();
  const me = orgStructure.data.employees?.find(x => !!x.user_id && x.user_id === user?._id);

  const events = useLoadedList<AbsenceEvent>(AbsenceEventsApiPath, {
    noLimit: true,
    noView: true,
    extraParams: {
      date_from: calendarControl.startDateStr,
      date_to: calendarControl.endDateStr,
      ...(includeCancelled.value === "true" ? {} : { active_only: "true" }),
    }
  });

  const eventBuckets = useAbsenceEventBuckets(
    events?.data?.filter(e => e.kind !== AbsenceKindNote) || EmptyEvents, 
    calendarControl.startDateStr, 
    calendarControl.endDateStr
  );

  const notesBuckets = useAbsenceEventBuckets(
    events?.data?.filter(e => e.kind === AbsenceKindNote) || EmptyEvents, 
    calendarControl.startDateStr, 
    calendarControl.endDateStr
  );

  const newItem = useEditItem2<Partial<AbsenceEvent>>({
    save: async (item) => {
      const itemX = { ...item };
      if(!itemX.end_date) {
        itemX.end_date = itemX.start_date;
      }
      const result = await apiFetch<AbsenceEvent>(AbsenceEventsApiPath, "post", itemX);
      events.reload();
      return result;
    },
  });

  const editItem = useEditItem2<AbsenceEvent>({
    save: async (item) => {
      return item;
    },
  });

  const cancelItem = useEditItem2<Pick<AbsenceEvent, "_id" | "comment" | "kind">>({
    save: async (item) => {
      await apiFetch<AbsenceEvent>(`${AbsenceEventsApiPath}/${item._id}/cancel`, "post", { comment: item.comment });
      if(editItem.item?._id === item._id) {
        editItem.cancel();
      }
      events.reload();
      return item;
    }
  });


  const absenceStats = useEditItem2<{ stats?: AbsenceStats, employee: Employee }>({
  });
  const showStatsFor = useItemActionWithConfirmation<Employee, {}>(async (item) => {
    absenceStats.setItem({ employee: item });
    const stats = await apiFetch<AbsenceStats>(`/api/org/absence/stats/${item._id}`, "get");
    absenceStats.setItem({ employee: item, stats });
    return {};
  }, {
    skipConfirmation: true,
  });

  const extraQuotas = useAbsenceExtraQuotas();

  const staffingWarnings = useDepartmentStaffingWarnings(orgStructure.data, eventBuckets, calendarControl.startDateStr, calendarControl.endDateStr);
  
  const hasUpcomingWarningMyDept = useMemo(() => {
    const myDepartments = orgStructure.data.departments.filter(x => !!x.employees?.find(e => e._id === me?._id));
    if (!staffingWarnings.length || !myDepartments.length)
      return false;

    // 1. Create a fast-lookup Set of your department IDs
    const myDeptIds = new Set(myDepartments.map(d => d._id));

    // 2. Calculate today and today + 6 days in local YYYY-MM-DD format
    const getLocalYYYYMMDD = (d: Date) => {
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    const todayDate = new Date();
    const todayStr = getLocalYYYYMMDD(todayDate);

    const nextWeekDate = new Date();
    nextWeekDate.setDate(todayDate.getDate() + 6);
    const nextWeekStr = getLocalYYYYMMDD(nextWeekDate);

    // 3. Lazily find the first matching warning
    return staffingWarnings.some(warning => 
      myDeptIds.has(warning.department_id) && 
      warning.date >= todayStr && 
      warning.date <= nextWeekStr
    );
  }, [staffingWarnings, orgStructure.data.departments, me?._id]);


  return {
    isLoading: orgStructure.isLoading || events.isLoading,
    calendar: calendarControl,
    orgStructure: {
      ...orgStructure.data,
      departments: onlyMyDepartmentFilter.value === "true"
        ? orgStructure.data.departments.filter(x => !!x.employees?.find(e => e._id === me?._id))
        : orgStructure.data.departments,
    },
    orgStructureManagement,
    filters: {
      onlyMyDepartment: wrapBooleanFilter(onlyMyDepartmentFilter),
      includeCancelled: wrapBooleanFilter(includeCancelled),
    },

    events,
    eventBuckets,
    notesBuckets,
    staffingWarnings,
    hasUpcomingWarningMyDept,

    newItem: {
      ...newItem,
      start: (item?: Partial<AbsenceEvent>) => {
        newItem.startEditing({
          owner_id: me?._id,
          kind: "remote",
          start_date: today(),
          ...(item || {}),
        })
      }
    },

    absenceStats: {
      ...absenceStats,
      isLoading: showStatsFor.isRunning,
      showFor: (employee: Employee) => showStatsFor.run(employee),
      showForMe: () => {
        if(me) {
          showStatsFor.run(me);
        }
      },
    },

    extraQuotas,

    editItem,
    cancelItem,
  }
}


export type AbsenceCalendarData = ReturnType<typeof useAbsenceCalendar>;
